import React, { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import DropDownItem from '../DropDown/drop-down-item-component';
import useHandleRedirect from '../../Hooks/useHandleRedirect';
import { ButtonPrimary, ButtonSecondary } from '../Buttons/button-component';
import { getNavbarData } from '../../Public/navbar-data.js';
import SystemDataService from '../../Services/SystemDataService';
import MediaComponent from '../Media/media-component.js';
import * as authActions from '../../Redux/Auth/AuthActions';

const ModalNavbar = ({ formatter, onTrigger }) => {
    const [openNavigation, setOpenNavigation] = useState(false);
    // menuStack handles: Level 0 (Main) -> Level 1 (Categories) -> Level 2 (Products)
    const [menuStack, setMenuStack] = useState([]);
    const handleRedirect = useHandleRedirect();
    const dispatch = useDispatch();

    const systemData = useSelector((state) => state.systemData || {});
    const { isAuthenticated, user } = useSelector((state) => state.auth);
    const { dir = 'ltr', language: lang = 'en', links = [] } = systemData;

    const isAdmin = user?.role === 'admin';
    const displayName = user?.name || user?.email || '';
    const initials = displayName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || (isAdmin ? 'AD' : 'ME');
    const dashboardPath = isAdmin ? '/admin' : '/home';
    const dashboardLabel = isAdmin ? 'Admin Panel' : 'My Account';

    const t = useMemo(() => getNavbarData(lang), [lang]);
    const isRtl = dir === 'rtl';

    const phoneData = SystemDataService.byLabel(links, 'Phone');
    const logoLink = SystemDataService.byLabel(links, 'Logo Full') || SystemDataService.byLabel(links, 'Logo');

    // --- SCROLL LOCK ---
    useEffect(() => {
        const target = document.documentElement;
        if (openNavigation) {
            target.style.overflow = 'hidden';
            document.body.style.overflow = 'hidden';
        } else {
            target.style.overflow = '';
            document.body.style.overflow = '';
        }
    }, [openNavigation]);

    const topLevelItems = useMemo(() => {
        if (!formatter) return [];
        return Array.isArray(formatter) ? formatter : formatter?.elements || [];
    }, [formatter]);

    const closeAll = useCallback(() => {
        setOpenNavigation(false);
        setTimeout(() => setMenuStack([]), 400);
    }, []);

    // --- FIX: Logic to prevent accidental modal closing ---
    const handleMainTrigger = (evt) => {
        // We use currentTarget to ensure we get the button's value even if an icon was clicked
        const rawValue = evt?.currentTarget?.value || '';
        const href = typeof rawValue === 'string' && rawValue.includes(',')
            ? rawValue.split(',')[1]
            : rawValue;

        const item = topLevelItems.find((it) => it?.value === href);

        // If item has children, push to stack and STOP (do not navigate/close)
        if (item && Array.isArray(item.children) && item.children.length > 0) {
            setMenuStack([item]);
            return;
        }

        // Simple link logic
        if (onTrigger) {
            onTrigger(evt);
        } else if (href) {
            handleRedirect(evt, href);
        }
        closeAll();
    };

    const handleChildClick = (item) => {
        if (item.href) {
            handleRedirect({ preventDefault: () => { } }, item.href);
        }
        closeAll();
    };

    const goBack = () => setMenuStack(prev => prev.slice(0, -1));

    return (
        <div className="flex items-center" dir={dir}>

            {/* --- TRIGGER BUTTON --- */}
            <button
                className="relative z-[70] p-1 active:scale-90 transition-transform"
                onClick={() => (openNavigation ? closeAll() : setOpenNavigation(true))}
            >
                <div className={`${openNavigation ? 'opacity-0 scale-0 absolute' : 'opacity-100 scale-100 relative'} transition-all duration-300`}>
                    <svg className="h-7 w-7 fill-current text-white" viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
                    </svg>
                </div>
                <div className={`${openNavigation ? 'opacity-100 scale-100 relative' : 'opacity-0 scale-0 absolute'} transition-all duration-300`}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="h-8 w-8 text-primary-500">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </div>
            </button>

            {/* --- NAVIGATION OVERLAY --- */}
            <div className={`${openNavigation ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'} fixed inset-0 w-screen h-[100dvh] bg-white transition-opacity duration-300 z-[60] flex flex-col`}>

                <div className="relative flex-1 flex flex-col min-h-0">

                    {/* LEVEL 0: ROOT */}
                    <div className={`absolute inset-0 px-8 pt-32 pb-12 overflow-y-auto transition-all duration-500 ease-out 
                        ${menuStack.length > 0 ? (isRtl ? 'translate-x-1/4 opacity-0' : '-translate-x-1/4 opacity-0') : 'translate-x-0 opacity-100'}`}>

                        <DropDownItem
                            elements={{ elements: topLevelItems }}
                            onTrigger={handleMainTrigger}
                            fontStyle="font-black uppercase tracking-widest"
                            fontColor="text-primary-950"
                            fontSize="text-3xl md:text-4xl"
                            containerClass="flex flex-col items-start space-y-6"
                        />

                        {/* Brand Footer + User Section */}
                        <div className="mt-12 pt-8 border-t border-gray-100 flex flex-col items-start gap-6">
                            <img src={logoLink?.image} alt="BH Logo" className="h-10 w-auto object-contain grayscale opacity-30" />

                            {isAuthenticated ? (
                                /* ── Logged-in user card ── */
                                <div className="w-full rounded-2xl border border-gray-100 bg-gray-50 overflow-hidden">
                                    <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-100">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-black shrink-0 ${isAdmin ? 'bg-red-500' : 'bg-primary-500'}`}>
                                            {initials}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            {user?.name && <p className="text-sm font-black text-primary-950 truncate">{user.name}</p>}
                                            <p className="text-[10px] text-gray-400 truncate">{user?.email}</p>
                                        </div>
                                        <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-1 rounded-full shrink-0 ${isAdmin ? 'bg-red-100 text-red-600' : 'bg-primary-100 text-primary-600'}`}>
                                            {isAdmin ? 'Admin' : 'Customer'}
                                        </span>
                                    </div>
                                    <div className="flex">
                                        <button
                                            onClick={() => { handleRedirect({ preventDefault: () => { } }, dashboardPath); closeAll(); }}
                                            className="flex-1 py-3 text-[10px] font-black uppercase tracking-widest text-primary-600 hover:bg-gray-100 transition-colors border-r border-gray-100 text-center"
                                        >
                                            {dashboardLabel}
                                        </button>
                                        <button
                                            onClick={() => { dispatch(authActions.logout()); closeAll(); }}
                                            className="flex-1 py-3 text-[10px] font-black uppercase tracking-widest text-red-500 hover:bg-red-50 transition-colors text-center"
                                        >
                                            Sign Out
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                /* ── Guest sign-in prompt ── */
                                <ButtonPrimary
                                    onClick={() => { handleRedirect({ preventDefault: () => { } }, '/login'); closeAll(); }}
                                    className="w-full !py-5 !rounded-full text-[10px] font-black uppercase tracking-widest shadow-sm"
                                >
                                    Sign In / Register
                                </ButtonPrimary>
                            )}

                            {phoneData?.url && (
                                <ButtonSecondary href={phoneData.url} className="w-full !py-5 !rounded-full text-[10px] font-black uppercase tracking-widest shadow-sm">
                                    {phoneData.description}
                                </ButtonSecondary>
                            )}
                        </div>
                    </div>

                    {/* DYNAMIC LEVELS (Categories / Products) */}
                    {menuStack.map((activeParent, index) => {
                        const isTopPanel = index === menuStack.length - 1;
                        return (
                            <div
                                key={index}
                                className={`absolute inset-0 h-full w-full bg-white transition-transform duration-500 ease-out flex flex-col 
                                ${isTopPanel ? 'translate-x-0' : (isRtl ? 'translate-x-full' : '-translate-x-full')}`}
                                style={{ zIndex: 65 + index }}
                            >
                                {/* Level Header */}
                                <div className="px-8 pt-28 pb-4 border-b-2 border-secondary-500 shrink-0 flex items-center justify-between">
                                    <button onClick={goBack} className="p-2 bg-gray-50 rounded-full text-primary-500 active:scale-95 transition-all">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className={isRtl ? 'rotate-180' : ''}>
                                            <path d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>
                                    <p className="text-lg font-black text-primary-950 uppercase tracking-widest truncate ml-4">
                                        {activeParent.label || activeParent.name}
                                    </p>
                                </div>

                                {/* Level Content */}
                                <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">

                                    {/* Sub-Category Preview */}
                                    {(activeParent.img || activeParent.image) && (
                                        <div className="mb-8 rounded-[2.5rem] overflow-hidden shadow-xl h-44 w-full border border-gray-100 bg-gray-50 flex items-center justify-center p-8">
                                            <img
                                                src={activeParent.img || activeParent.image}
                                                alt={activeParent.label || activeParent.name}
                                                className="max-h-full max-w-full object-contain mix-blend-multiply"
                                            />
                                        </div>
                                    )}

                                    <ul className="space-y-3">
                                        {(activeParent.children || []).map((child, idx) => (
                                            <li key={idx}>
                                                <button
                                                    onClick={() => handleChildClick(child)}
                                                    className="w-full flex items-center gap-4 p-3 rounded-[1.5rem] bg-gray-50 hover:bg-gray-100 active:bg-gray-200 transition-all text-left group"
                                                >
                                                    <div className="w-12 h-12 shrink-0 rounded-xl bg-white border border-gray-100 overflow-hidden flex items-center justify-center">
                                                        <MediaComponent
                                                            media={child.img || child.image}
                                                            quality={30}
                                                            objectFit='fit'
                                                            wrapperClassName="w-full h-full flex items-center justify-center"
                                                            useSekeleton={false}
                                                        />
                                                    </div>
                                                    <div className="flex flex-col flex-1">
                                                        <span className="text-sm font-bold text-primary-950 uppercase leading-tight">
                                                            {child.label}
                                                        </span>
                                                        {child.description && (
                                                            <span className="text-[10px] text-gray-400 font-medium line-clamp-1 mt-0.5">{child.description}</span>
                                                        )}
                                                    </div>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className={`text-gray-300 shrink-0 ${isRtl ? 'rotate-180' : ''}`}>
                                                        <path d="M9 5l7 7-7 7" />
                                                    </svg>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* View All Collection Button */}
                                    {activeParent.href && (
                                        <div className="mt-10 pb-12">
                                            <ButtonPrimary
                                                onClick={() => { handleRedirect({ preventDefault: () => { } }, activeParent.href); closeAll(); }}
                                                className="!w-full !rounded-full font-black uppercase tracking-widest py-5 text-[10px] shadow-lg shadow-primary-500/10"
                                            >
                                                {isRtl ? 'צפה בהכל' : 'Explore All'} {activeParent.label || activeParent.name}
                                            </ButtonPrimary>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default React.memo(ModalNavbar);